// Query params that only say which view a page shows, plus campaign tags.
// Everything else is dropped: reset and invite tokens, emails, search text and
// session ids all ride in links at times.
const ROUTE_PARAMS = new Set([
  "sort",
  "stash_sort",
  "category",
  "collection",
  "scope",
  "tag",
  "tagLogic",
  "layer",
  "group",
  "entity_type",
  "view",
  "date",
  "platform",
]);
const URL_PROPERTY = /(^|_)(url|href|referrer|pathname|path)($|_)/i;

/** Analytics may describe a route, but never carry the credentials or private
 * input that can accompany a link. Also covers SDK-added current/initial URLs
 * and password-reset tokens in path segments. */
export function sanitizeAnalyticsUrl(value: string): string {
  if (!value || value === "$direct") return value;
  try {
    const absolute = /^[a-z][a-z\d+.-]*:/i.test(value);
    const url = new URL(value, "https://analytics.invalid");
    url.username = "";
    url.password = "";
    url.hash = "";
    url.pathname = url.pathname.replace(/(\/reset-password\/)[^/]+/gi, "$1redacted");
    for (const name of [...url.searchParams.keys()]) {
      if (!ROUTE_PARAMS.has(name) && !name.startsWith("utm_")) url.searchParams.delete(name);
    }
    return absolute ? url.href : `${url.pathname}${url.search}`;
  } catch {
    return "redacted";
  }
}

/** SDK person properties can nest initial URLs under $set and $set_once. */
export function sanitizeAnalyticsUrls(properties: Record<string, unknown>, depth = 0): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(properties).map(([key, value]) => {
      if (typeof value === "string" && URL_PROPERTY.test(key.replace(/^\$/, "")))
        return [key, sanitizeAnalyticsUrl(value)];
      if (value && typeof value === "object" && !Array.isArray(value) && depth < 3)
        return [key, sanitizeAnalyticsUrls(value as Record<string, unknown>, depth + 1)];
      return [key, value];
    }),
  );
}

/** A route's path and query, split again after scrubbing. */
export function sanitizeRoute(pathname: string, search: string): { pathname: string; search: string } {
  const page = sanitizeAnalyticsUrl(`${pathname}${search}`);
  const queryAt = page.indexOf("?");
  return queryAt < 0
    ? { pathname: page, search: "" }
    : { pathname: page.slice(0, queryAt), search: page.slice(queryAt) };
}
